/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 * @author IshakaW
 * 01/28/2022
 *
 */

import React from 'react';
import '../../form-elements/formElement.css';
import API from "../../../../../utils/api";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import axios from 'axios';
import FormElementGenerator from "../../form-elements/formElementGenerator";
import Loader from "../../../loader/loader";

/**
 * HOC for the lms form
 */
class LmsUrlForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            lmsUrlId: null,
            lmsUrlLink: '',
            // lms dropdown
            lmsId:'',
            lmsName: '',
            // lms url type dropdown
            lmsUrlTypeId: '',
            lmsUrlTypeName: '',
            isDisplayLoader: true,
            errors: {},

            token: '',
            fields: [
                {
                    inputType: 'textUrl',
                    labelName: 'Url *',
                    inputName: 'lmsUrlLink',
                    placeholder: 'Please enter lms url here',
                    required: true
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Lms *',
                    inputName: 'lmsId',
                    optionValueName: 'lmsId',
                    optionLabelName: 'lmsName',
                    required: true,
                    path: '/lms-urls',
                    valueType: 'lms'
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Lms Url Type *',
                    inputName: 'lmsUrlTypeId',
                    optionValueName: 'lmsUrlTypeId',
                    optionLabelName: 'lmsUrlTypeName',
                    required: true,
                    path: '/lms-urls',
                    valueType: 'lmsUrlTypes'
                }
            ]
        };

        // get the parameter from URL
        const lmsUrlId = this.props.match.params.id;

        // set icons to the header
        if (lmsUrlId === 'create') {
            EventEmitter.emit('setPageHeaderData', {
                page: 'lmsUrls',
                pageName: "Lms Urls",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/lmsUrls",
                optionalIcons: ['backIcon','clearFormIcon']
            });
        } else {
            EventEmitter.emit('setPageHeaderData', {
                page: 'lmsUrls',
                pageName: "Lms Urls",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/lmsUrls",
                optionalIcons: ['backIcon','clearFormIcon','deleteIcon']
            });
        }

        // set the event emitters
        EventEmitter.on('saveFormData', this.handleSubmit);
        EventEmitter.on('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.on('deleteFormData', this.deleteLmsUrl);
        EventEmitter.on('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.on('clearForm', this.clearForm);
    }

    // set a token to unsubscribe the async call
    CancelToken = axios.CancelToken;
    source = this.CancelToken.source();

    componentDidMount = async () => {
        // get parameter from URL
        const lmsUrlId = this.props.match.params.id;

        // get token
        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = Object.assign({}, this.state);

        // fill the form if not a create lms
        if (lmsUrlId !== 'create') {

            const url = '/lms-urls?search=%7BlmsUrlId:' + lmsUrlId + '%7D';

            await API.get(url, {headers: {Authorization: token}, cancelToken: this.source.token})
                .then(res => {
                    const data = res.data[0];
                    oldState.lmsUrlId = (data.lmsUrlId !== undefined) ? data.lmsUrlId : null;
                    oldState.lmsUrlLink = (data.lmsUrlLink !== undefined) ? data.lmsUrlLink : '';
                    oldState.lmsId = (data.lmsId !== undefined) ? data.lmsId : '';
                    oldState.lmsName = (data.lmsName !== undefined) ? data.lmsName : '';
                    oldState.lmsUrlTypeId = (data.lmsUrlTypeId !== undefined) ? data.lmsUrlTypeId : '';
                    oldState.lmsUrlTypeName = (data.lmsUrlTypeName !== undefined) ? data.lmsUrlTypeName : '';
                    oldState.token = token;
                    oldState.isDisplayLoader = false;

                    this.setState(oldState);
                })
                .catch(er => {
                    oldState.isDisplayLoader = false;
                    this.setState(oldState);
                    this.displayErrorsForAPICalls(er);
                });
        } else {
            oldState.token = token;
            oldState.isDisplayLoader = false;
            this.setState(oldState);
        }
    };

    componentWillUnmount = () => {
        // remove all event emitters
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('saveFormData', this.handleSubmit);
        EventEmitter.off('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.off('deleteFormData', this.deleteLmsUrl);
        EventEmitter.off('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.off('clearForm', this.clearForm);
        this.source.cancel("cancel lms url async calls");
    };

    /**
     * set the state
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionValueName]: event.value,
                [event.optionLabelName]: event.label
            }, () => {
                this.validate(event.optionValueName, event.value)
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            }, () => {
                this.validate(key, val)
            });
        }
    };

    /**
     * handle the form submission
     */
    handleSubmit = () => {
        // data to be sent
        let data = null;
        // check its a update or create
        if (this.state.lmsUrlId !== null) {
            data = {
                lmsUrlId: this.state.lmsUrlId,
                lmsUrlLink: this.state.lmsUrlLink,
                lmsId: Number(this.state.lmsId),
                lmsUrlTypeId: Number(this.state.lmsUrlTypeId)
            };
        } else {
            data = {
                lmsUrlLink: this.state.lmsUrlLink,
                lmsId: Number(this.state.lmsId),
                lmsUrlTypeId: Number(this.state.lmsUrlTypeId)
            };
        }

        // validate each form field
        for (let [key, value] of Object.entries(data)) {
            this.validate(key, value);
        }

        // check weather there are error messages
        if (Object.values(this.state.errors).find(o => o !== '') === undefined) {
            // check update or create
            if (this.state.lmsUrlId !== null) {
                this.updateLmsUrl(data);
            } else {
                this.createLmsUrl(data);
            }
        }
    };

    /**
     * validate the user inputs
     * @param key - input name
     * @param value - input value
     */
    validate = (key, value) => {
        let error = {};

        switch (key) {
            case 'lmsUrlLink':
                if (value === '')
                    error[key] = "URL is Required!";
                else if (value === ' ')
                    error[key] = "URL can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 128))
                    error[key] = "URL can't have more than 128 characters!";
                else if ((value.indexOf("https://") === 0) || (value.indexOf("http://") === 0) || (value.indexOf("/") === 0))
                    error[key] = "";
                else
                    error[key] = "URL must begin with with http://, https:// or / ";
                break;
            case 'lmsId':
                if (this.state.lmsId === '')
                    error[key] = "Please select a Lms!";
                else
                    error[key] = "";
                break;
            case 'lmsUrlTypeId':
                if (this.state.lmsUrlTypeId === '')
                    error[key] = "Please select a Lms URL Type!";
                else
                    error[key] = "";
                break;
            default:
                break;
        }

        const newState = Object.assign({}, this.state);
        newState.errors = Object.assign(newState.errors, error);

        this.setState(newState);
    };

    /**
     * create lms url
     * @param data - the data to create a new lms url
     */
    createLmsUrl = async (data) => {
        await API.post('/lms-urls',
            {
                "lmsUrlLink": data.lmsUrlLink,
                "lmsId": data.lmsId,
                "lmsUrlTypeId": data.lmsUrlTypeId
            }, {headers: {Authorization: this.state.token}, cancelToken: this.source.token}
        ).then(res => {
            this.setAlert("Success", "Success", "Successfully Created the Lms Url");
            this.props.history.push('/home/lmsUrls');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    /**
     * update lms url
     * @param data - updated lms url details
     */
    updateLmsUrl = async (data) => {
        await API.put('/lms-urls',
            {
                "lmsUrlId": data.lmsUrlId,
                "lmsUrlLink": data.lmsUrlLink,
                "lmsId": data.lmsId,
                "lmsUrlTypeId": data.lmsUrlTypeId
            },{headers: {Authorization: this.state.token}, cancelToken: this.source.token}
        ).then(response => {
            this.setAlert("Success", "Success", "Successfully updated the Lms Url");
            this.props.history.push('/home/lmsUrls');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    /**
     * display delete warning message
     */
    displayDeleteWarningMessage = () => {
        if (this.state.lmsUrlId !== null) {
            EventEmitter.emit('warningModelData', {
                type: 'lmsUrls',
                name: 'this Lms Url'
            });
            EventEmitter.emit('openWarningModel', true);
        } else {
            EventEmitter.emit('setAlert', {
                openAlert: true,
                type: "Error",
                isDisplayAlertIcon: true,
                alertMessage: "You are trying to delete before loading the Lms Url",
                alertTitle: "Error"
            });
        }

    };

    /**
     * delete a lms
     * @param data
     */
    deleteLmsUrl = async (data) => {
        if (data && (this.props.match.path === "/home/lmsUrls/:id")) {
            await API.delete('/lms-urls?lmsUrlId=' + this.state.lmsUrlId, {headers: {Authorization: this.state.token}, cancelToken: this.source.token})
                .then(res => {
                    this.setAlert("Success", "Success", "Successfully Deleted the Lms Url");
                    this.props.history.push('/home/lmsUrls');
                }).catch(er => {
                    this.displayErrorsForAPICalls(er);
                });
        }
    };

    displayErrorsForAPICalls = (error) => {
        if (error.response !== undefined && error.response.status === 404) {
            this.setAlert("Error", "Error", "The selected item not found!");
        } else if (error.message.includes('422')) {
            this.setAlert("Error", "Sorry", "LMS Url is already exist!");
        } else if (error.response === undefined || error.response.status === 401) {
            localStorage.removeItem('tmcUser')
            this.props.history.push('/');
        } else {
            this.setAlert("Error", "Error", "Something went wrong");
        }
    };

    /**
     * fire an alert
     * @param alertType
     * @param alertTitle
     * @param alertMessage
     */
    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    /**
     * clear the form
     */
    clearForm = () => {
        const newState = Object.assign({}, this.state);

        newState.lmsUrlLink = "";
        newState.lmsId = "";
        newState.lmsName = "";
        newState.lmsUrlTypeId = "";
        newState.lmsUrlTypeName = "";
        newState.errors = {};

        this.setState(newState);
    };

    /**
     * navigate to previous page
     */
    navigateToPreviousPage = () => {
        this.props.history.push('/home/lmsUrls');
    };

    render() {
        return(
            <div role="form" className="container">
                {this.state.isDisplayLoader && <Loader/>}
                <div className="main-btn-container">
                    <div className="buttons" tooltip="Save" onClick={this.handleSubmit}>
                        <i className="fa fa-save fa-lg icon-primary" />
                    </div>
                </div>
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
            </div>
        )
    }
}

export default LmsUrlForm;
